// /src/pages/ManageExtraQuestions.jsx

import React, { useState, useEffect } from 'react';
import { getLeaderTemplate, saveLeaderTemplate, getSystemStatus, getLeaderModules } from '../../api/moduleApi';
import QuestionTemplateManager from '../../components/ui/QuestionTemplateManager.jsx';

const DEFAULT_LEADER_TEMPLATE = [];

// Expect `user` prop containing the authenticated user (with `id`).
const ManageExtraQuestions = ({ user, selectedLeader }) => {
  const leaderId = selectedLeader?.account_id || user?.id || null;
  const [leaderModules, setLeaderModules] = useState([]);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [loadingModules, setLoadingModules] = useState(true);

  // --- Fetch Leader Modules on load ---
  useEffect(() => {
    const fetchModules = async () => {
      setLoadingModules(true);
      if (!leaderId) {
        setLeaderModules([]);
        setSelectedModuleId(null);
        setLoadingModules(false);
        return;
      }
      try {
        const modules = await getLeaderModules(leaderId);
        setLeaderModules(modules);
        if (modules.length > 0) {
          // Auto-select the first module for convenience
          setSelectedModuleId(modules[0].module_id);
        } else {
          setSelectedModuleId(null);
        }
      } catch (error) {
        console.error("Error fetching leader modules:", error);
      } finally {
        setLoadingModules(false);
      }
    };
    fetchModules();
  }, [leaderId]);

  if (loadingModules) {
    return <div className="text-center py-10 text-indigo-500 font-medium">Loading Modules...</div>;
  }

  if (leaderModules.length === 0) {
    return (
      <div className="p-6 bg-yellow-100 rounded-xl shadow-lg text-yellow-700 font-medium">
        You are not currently assigned as the leader for any modules.
      </div>
    );
  }

  // Pass the selectedModuleId to the manager component
  return (
    <div className="space-y-6">
      <div className="p-4 bg-white rounded-xl shadow-lg border border-indigo-200">
        <h3 className="text-xl font-semibold text-indigo-700 mb-2">Select Module</h3>
        <select
          value={selectedModuleId || ''}
          onChange={(e) => setSelectedModuleId(Number(e.target.value))}
          className="w-full p-2 border border-gray-300 rounded-lg text-base focus:ring-indigo-500 focus:border-indigo-500"
        >
          {leaderModules.map(module => (
            <option key={module.module_id} value={module.module_id}>
              {module.module_name} (Semester {module.semester})
            </option>
          ))}
        </select>
      </div>

      {/* Render the manager ONLY if a module is selected */}
      {selectedModuleId && (
          <QuestionTemplateManager
          key={selectedModuleId} // Key ensures the component fully resets when module changes
          title={`Extra Questions Template for Module ID: ${selectedModuleId}`}
          description="Manage additional questions that will appear alongside the master template for this specific module."
          // Pass the module ID to the API functions
          getTemplateFn={() => getLeaderTemplate(selectedModuleId)}
          saveTemplateFn={(templateData) => saveLeaderTemplate(selectedModuleId, templateData, leaderId)}
          getSystemStatusFn={getSystemStatus}
          defaultTemplate={DEFAULT_LEADER_TEMPLATE}
          templateType="extra"
        />
      )}
    </div>
  );
};

export default ManageExtraQuestions;